/** Basic consent mode: no Google script or requests until analytics is allowed. */
type AnalyticsWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: unknown[]) => void;
  [key: `ga-disable-${string}`]: boolean;
};

const publicPath = (path: string) =>
  path === "/" ||
  /^\/(docs|blog)(\/|$)/.test(path) ||
  ["/terms", "/privacy-policy", "/cookies", "/trust", "/dpa", "/data-request"].includes(path);

/** Updates an existing tag immediately without recording another page view. */
export const updateGoogleAnalyticsConsent = (
  allowed: boolean,
  marketingAllowed: boolean,
  measurementId = import.meta.env.PUBLIC_GOOGLE_ANALYTICS_ID,
) => {
  if (!measurementId || !/^G-[A-Z0-9]+$/.test(measurementId)) return;
  const analytics = window as unknown as AnalyticsWindow;
  analytics[`ga-disable-${measurementId}`] = !allowed;
  const adConsent = allowed && marketingAllowed ? "granted" : "denied";
  analytics.gtag?.("consent", "update", {
    analytics_storage: allowed ? "granted" : "denied",
    ad_storage: adConsent,
    ad_user_data: adConsent,
    ad_personalization: "denied",
  });
};

const prepareAnalytics = (
  allowed: boolean,
  pathname: string,
  measurementId: string | undefined,
  marketingAllowed = false,
) => {
  if (!measurementId || !/^G-[A-Z0-9]+$/.test(measurementId)) return;
  const analytics = window as unknown as AnalyticsWindow;
  const enabled = allowed;
  updateGoogleAnalyticsConsent(allowed, marketingAllowed, measurementId);
  const adConsent = enabled && marketingAllowed ? "granted" : "denied";
  if (!enabled) return;

  const location = new URL(pathname, window.location.origin);
  if (marketingAllowed && publicPath(pathname)) {
    const query = new URLSearchParams(window.location.search);
    for (const key of ["gclid", "gbraid", "wbraid"]) {
      const value = query.get(key);
      if (value && /^[A-Za-z0-9_-]{1,512}$/.test(value)) location.searchParams.set(key, value);
    }
  }
  const pageLocation = location.href;
  // Only our published campaign labels may cross the analytics boundary. Never send arbitrary
  // query parameters, account information or auth callback URLs. Ad click IDs require separate consent.
  const query = new URLSearchParams(window.location.search);
  const campaign = query.get("utm_campaign");
  const medium = query.get("utm_medium");
  const campaignFields =
    query.get("utm_source") === "google" &&
    ((campaign === "launch-search" && medium === "cpc") ||
      (campaign === "launch-in-app" && medium === "display"))
      ? { campaign_source: "google", campaign_medium: medium, campaign_name: campaign }
      : {};
  if (!analytics.gtag) {
    analytics.dataLayer = [];
    analytics.gtag = function () {
      // biome-ignore lint/complexity/noArguments: Google tag commands require the standard arguments queue.
      analytics.dataLayer!.push(arguments);
    };
    analytics.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    analytics.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: adConsent,
      ad_user_data: adConsent,
      ad_personalization: "denied",
    });
    analytics.gtag("set", {
      page_location: pageLocation,
      page_referrer: "",
      page_title: pathname,
      ...campaignFields,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    analytics.gtag("js", new Date());
    analytics.gtag("config", measurementId, {
      send_page_view: false,
      cookie_domain: window.location.hostname,
      cookie_flags: "SameSite=Lax;Secure",
    });
    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    document.head.appendChild(script);
  }
  analytics.gtag("set", {
    ...campaignFields,
    page_location: pageLocation,
    page_referrer: "",
    page_title: pathname,
  });
  return { analytics, pageLocation };
};

export const updateGoogleAnalytics = (
  allowed: boolean,
  pathname: string,
  measurementId = import.meta.env.PUBLIC_GOOGLE_ANALYTICS_ID,
  marketingAllowed = false,
) => {
  const prepared = prepareAnalytics(
    allowed && publicPath(pathname),
    pathname,
    measurementId,
    marketingAllowed,
  );
  if (!prepared) return;
  const { analytics, pageLocation } = prepared;
  analytics.gtag!("event", "page_view", {
    send_to: measurementId,
    page_location: pageLocation,
    page_referrer: "",
    page_title: pathname,
  });
};

/** Called only after the server confirms a new account and a valid sign-in session. */
export const trackGoogleAnalyticsSignup = (
  allowed: boolean,
  measurementId = import.meta.env.PUBLIC_GOOGLE_ANALYTICS_ID,
  marketingAllowed = false,
): Promise<void> => {
  if (!allowed) return Promise.resolve();
  const prepared = prepareAnalytics(true, "/signup-complete", measurementId, marketingAllowed);
  if (!prepared) return Promise.resolve();
  const { analytics } = prepared;
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      if (!publicPath(window.location.pathname)) analytics[`ga-disable-${measurementId}`] = true;
      resolve();
    };
    // Analytics blockers must never prevent account access or leave sign-in waiting indefinitely.
    const timer = setTimeout(finish, 1200);
    analytics.gtag!("event", "sign_up", {
      send_to: measurementId,
      page_location: `${window.location.origin}/signup-complete`,
      page_referrer: "",
      page_title: "signup-complete",
      event_callback: finish,
      event_timeout: 1000,
    });
  });
};
